import React, { useState, useEffect, useCallback } from 'react';
import { Shield, Globe, FileText, Plus, Edit, Trash2, Save, ChevronDown, ChevronUp, GripVertical, Eye, EyeOff, X } from 'lucide-react';
import { legalApi } from '../lib/api';
import { toast } from 'sonner';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

/* ── Cookie Zone Manager ── */
const ZoneManager = () => {
  const [zones, setZones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingZone, setEditingZone] = useState(null);

  const fetchZones = useCallback(async () => {
    try {
      const d = await legalApi.ownerGetZones();
      setZones(d.zones || []);
    } catch (e) { toast.error('Failed to load zones'); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchZones(); }, [fetchZones]);

  const saveZone = async (zone) => {
    try {
      await legalApi.ownerUpdateZone(zone.zone_id, zone);
      toast.success('Zone updated');
      setEditingZone(null);
      fetchZones();
    } catch (e) { toast.error('Failed to update zone'); }
  };

  const createZone = async () => {
    try {
      await legalApi.ownerCreateZone({ name: 'New Zone' });
      toast.success('Zone created');
      fetchZones();
    } catch (e) { toast.error('Failed'); }
  };

  const deleteZone = async (zoneId) => {
    if (!window.confirm('Delete this zone?')) return;
    try {
      await legalApi.ownerDeleteZone(zoneId);
      toast.success('Zone deleted');
      fetchZones();
    } catch (e) { toast.error(e.message || 'Cannot delete core zones'); }
  };

  if (loading) return <div className="flex justify-center py-8"><div className="spinner" /></div>;

  return (
    <div className="space-y-4" data-testid="zone-manager">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <Globe className="w-5 h-5 text-[#8B5CF6]" /> Cookie Zones
          </h3>
          <p className="text-xs text-gray-500 mt-0.5">Configure consent rules by region</p>
        </div>
        <Button onClick={createZone} size="sm" className="bg-[#8B5CF6] hover:bg-[#7C3AED]" data-testid="create-zone-btn">
          <Plus className="w-4 h-4 mr-1" /> Add Zone
        </Button>
      </div>

      {zones.map(zone => {
        const isEditing = editingZone?.zone_id === zone.zone_id;
        const zData = isEditing ? editingZone : zone;

        return (
          <div key={zone.zone_id} className="admin-card overflow-hidden" data-testid={`zone-${zone.zone_id}`}>
            <div className="p-4 flex items-center justify-between cursor-pointer"
              onClick={() => setEditingZone(isEditing ? null : { ...zone })}>
              <div className="flex items-center gap-3">
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-xs font-bold ${zone.consent_model === 'opt-in' ? 'bg-amber-500/15 text-amber-400' : 'bg-emerald-500/15 text-emerald-400'}`}>
                  {zone.consent_model === 'opt-in' ? 'IN' : 'OUT'}
                </div>
                <div>
                  <p className="font-medium text-sm">{zone.name}</p>
                  <p className="text-[11px] text-gray-500">
                    {zone.countries?.length || 0} countries — {zone.cookie_categories?.length || 0} categories — {zone.consent_model}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {!['eu_eea', 'uk', 'usa', 'rest_of_world'].includes(zone.zone_id) && (
                  <button onClick={(e) => { e.stopPropagation(); deleteZone(zone.zone_id); }}
                    className="p-1.5 rounded hover:bg-red-500/10 text-gray-500 hover:text-red-400">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                )}
                {isEditing ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
              </div>
            </div>

            {isEditing && (
              <div className="border-t border-white/5 p-4 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs text-gray-400 mb-1 block">Zone Name</label>
                    <Input value={zData.name} onChange={e => setEditingZone({ ...zData, name: e.target.value })}
                      className="bg-[#030014] border-[#8B5CF6]/20 h-9 text-sm" />
                  </div>
                  <div>
                    <label className="text-xs text-gray-400 mb-1 block">Consent Model</label>
                    <Select value={zData.consent_model} onValueChange={v => setEditingZone({ ...zData, consent_model: v })}>
                      <SelectTrigger className="bg-[#030014] border-[#8B5CF6]/20 h-9"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-[#0F0B1E] border-[#8B5CF6]/20">
                        <SelectItem value="opt-in">Opt-In (explicit consent)</SelectItem>
                        <SelectItem value="opt-out">Opt-Out (implied consent)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div>
                  <label className="text-xs text-gray-400 mb-1 block">Countries (ISO codes, comma-separated)</label>
                  <Input value={(zData.countries || []).join(', ')}
                    onChange={e => setEditingZone({ ...zData, countries: e.target.value.split(',').map(s => s.trim().toUpperCase()).filter(Boolean) })}
                    className="bg-[#030014] border-[#8B5CF6]/20 h-9 text-sm font-mono" placeholder="IT, FR, DE, ES" />
                </div>

                <div>
                  <label className="text-xs text-gray-400 mb-1.5 block">Cookie Categories</label>
                  <div className="space-y-2">
                    {(zData.cookie_categories || []).map((cat, idx) => (
                      <div key={idx} className="flex items-center gap-2 p-2 rounded bg-white/[0.02] border border-white/5">
                        <Input value={cat.label} className="bg-transparent border-none h-7 text-xs flex-1 p-0 px-2"
                          onChange={e => {
                            const cats = [...zData.cookie_categories];
                            cats[idx] = { ...cats[idx], label: e.target.value };
                            setEditingZone({ ...zData, cookie_categories: cats });
                          }} />
                        <label className="flex items-center gap-1 text-[10px] text-gray-400 whitespace-nowrap">
                          <input type="checkbox" checked={cat.mandatory}
                            onChange={() => {
                              const cats = [...zData.cookie_categories];
                              cats[idx] = { ...cats[idx], mandatory: !cats[idx].mandatory, default_enabled: !cats[idx].mandatory ? true : cats[idx].default_enabled };
                              setEditingZone({ ...zData, cookie_categories: cats });
                            }} className="accent-[#8B5CF6]" /> Required
                        </label>
                        <label className="flex items-center gap-1 text-[10px] text-gray-400 whitespace-nowrap">
                          <input type="checkbox" checked={cat.default_enabled} disabled={cat.mandatory}
                            onChange={() => {
                              const cats = [...zData.cookie_categories];
                              cats[idx] = { ...cats[idx], default_enabled: !cats[idx].default_enabled };
                              setEditingZone({ ...zData, cookie_categories: cats });
                            }} className="accent-[#8B5CF6]" /> Default On
                        </label>
                        {!cat.mandatory && (
                          <button onClick={() => {
                            const cats = zData.cookie_categories.filter((_, i) => i !== idx);
                            setEditingZone({ ...zData, cookie_categories: cats });
                          }} className="text-gray-500 hover:text-red-400"><X className="w-3 h-3" /></button>
                        )}
                      </div>
                    ))}
                    <button onClick={() => {
                      const cats = [...(zData.cookie_categories || []), { key: `custom_${Date.now()}`, label: 'New Category', mandatory: false, default_enabled: false }];
                      setEditingZone({ ...zData, cookie_categories: cats });
                    }} className="text-xs text-[#8B5CF6] hover:text-[#7C3AED]">
                      <Plus className="w-3 h-3 inline mr-1" />Add category
                    </button>
                  </div>
                </div>

                <div className="flex justify-end">
                  <Button onClick={() => saveZone(zData)} size="sm" className="bg-[#00FF9D] hover:bg-[#00FF9D]/80 text-black">
                    <Save className="w-3.5 h-3.5 mr-1" /> Save Zone
                  </Button>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

/* ── Legal Pages Manager ── */
const LegalPagesManager = () => {
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingPage, setEditingPage] = useState(null);

  const fetchPages = useCallback(async () => {
    try {
      const d = await legalApi.ownerGetPages();
      setPages(d.pages || []);
    } catch (e) { toast.error('Failed to load pages'); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchPages(); }, [fetchPages]);

  const savePage = async () => {
    if (!editingPage) return;
    try {
      const payload = { ...editingPage };
      if (editingPage.page_id) {
        await legalApi.ownerUpdatePage(editingPage.page_id, payload);
      } else {
        await legalApi.ownerCreatePage(editingPage);
      }
      toast.success('Page saved');
      setEditingPage(null);
      fetchPages();
    } catch (e) { toast.error('Failed to save'); }
  };

  const deletePage = async (pageId) => {
    if (!window.confirm('Delete this page?')) return;
    try {
      await legalApi.ownerDeletePage(pageId);
      toast.success('Deleted');
      fetchPages();
    } catch (e) { toast.error('Failed'); }
  };

  if (loading) return <div className="flex justify-center py-8"><div className="spinner" /></div>;

  return (
    <div className="space-y-4" data-testid="legal-pages-manager">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <FileText className="w-5 h-5 text-[#00FF9D]" /> Legal Pages
          </h3>
          <p className="text-xs text-gray-500 mt-0.5">Manage privacy policy, terms of service, and more</p>
        </div>
        <Button onClick={() => setEditingPage({ title: '', slug: '', content: '', published: false, show_in_footer: false, order: pages.length })}
          size="sm" className="bg-[#8B5CF6] hover:bg-[#7C3AED]" data-testid="create-legal-page-btn">
          <Plus className="w-4 h-4 mr-1" /> New Page
        </Button>
      </div>

      {pages.map(page => (
        <div key={page.page_id} className="admin-card p-4 flex items-center justify-between" data-testid={`legal-page-${page.page_id}`}>
          <div className="flex items-center gap-3">
            <div className={`w-2 h-8 rounded-full ${page.published ? 'bg-[#00FF9D]' : 'bg-gray-600'}`} />
            <div>
              <p className="font-medium text-sm">{page.title}</p>
              <p className="text-[11px] text-gray-500">
                /legal/{page.slug} — v{page.version || 1}
                {page.show_in_footer && <span className="ml-2 text-[#8B5CF6]">In footer</span>}
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" className="h-7 text-xs border-[#8B5CF6]/30"
              onClick={() => setEditingPage({ ...page })} data-testid={`edit-legal-${page.page_id}`}>
              <Edit className="w-3 h-3 mr-1" /> Edit
            </Button>
            <Button size="sm" variant="outline" className="h-7 text-xs border-red-500/30 text-red-400"
              onClick={() => deletePage(page.page_id)}>
              <Trash2 className="w-3 h-3" />
            </Button>
          </div>
        </div>
      ))}

      {/* Editor Modal */}
      {editingPage && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={() => setEditingPage(null)}>
          <div className="admin-card p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="legal-page-editor">
            <h3 className="text-lg font-semibold mb-4">{editingPage.page_id ? 'Edit Legal Page' : 'New Legal Page'}</h3>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs text-gray-400 mb-1 block">Title</label>
                  <Input value={editingPage.title} onChange={e => setEditingPage({ ...editingPage, title: e.target.value })}
                    className="bg-[#030014] border-[#8B5CF6]/20" data-testid="legal-title-input" />
                </div>
                <div>
                  <label className="text-xs text-gray-400 mb-1 block">URL Slug</label>
                  <Input value={editingPage.slug} onChange={e => setEditingPage({ ...editingPage, slug: e.target.value })}
                    className="bg-[#030014] border-[#8B5CF6]/20 font-mono" placeholder="privacy-policy" />
                </div>
              </div>
              <div>
                <label className="text-xs text-gray-400 mb-1 block">Content (HTML)</label>
                <Textarea value={editingPage.content} onChange={e => setEditingPage({ ...editingPage, content: e.target.value })}
                  className="bg-[#030014] border-[#8B5CF6]/20 min-h-[200px] font-mono text-xs" />
              </div>
              <div className="flex flex-wrap items-center gap-6">
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="checkbox" checked={editingPage.published}
                    onChange={() => setEditingPage({ ...editingPage, published: !editingPage.published })}
                    className="accent-[#8B5CF6]" />
                  <Eye className="w-4 h-4 text-gray-400" /> Published
                </label>
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="checkbox" checked={editingPage.show_in_footer}
                    onChange={() => setEditingPage({ ...editingPage, show_in_footer: !editingPage.show_in_footer })}
                    className="accent-[#8B5CF6]" />
                  Show in Footer
                </label>
                {editingPage.page_id && (
                  <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="notify-users-checkbox">
                    <input type="checkbox" checked={!!editingPage.notify_users}
                      onChange={() => setEditingPage({ ...editingPage, notify_users: !editingPage.notify_users })}
                      className="accent-amber-400" />
                    <span className="text-amber-400">Notify Users</span>
                  </label>
                )}
                <div className="flex items-center gap-2">
                  <label className="text-xs text-gray-400">Order:</label>
                  <Input type="number" value={editingPage.order || 0}
                    onChange={e => setEditingPage({ ...editingPage, order: parseInt(e.target.value) || 0 })}
                    className="bg-[#030014] border-[#8B5CF6]/20 w-16 h-8 text-xs" />
                </div>
              </div>
              {editingPage.notify_users && (
                <div className="p-3 bg-amber-500/10 border border-amber-500/20 rounded-lg text-xs text-amber-400">
                  All existing users will be required to re-accept this policy after their next login.
                </div>
              )}
              <div className="flex gap-3 justify-end pt-2">
                <Button variant="outline" onClick={() => setEditingPage(null)}>Cancel</Button>
                <Button onClick={savePage} className="bg-[#00FF9D] hover:bg-[#00FF9D]/80 text-black" data-testid="save-legal-page-btn">
                  <Save className="w-4 h-4 mr-1" /> Save Page
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/* ── Main Legal & Privacy Panel ── */
const LegalPrivacyPanel = () => {
  return (
    <div className="space-y-8" data-testid="legal-privacy-panel">
      <div>
        <h2 className="text-xl font-bold flex items-center gap-2 mb-1">
          <Shield className="w-5 h-5 text-[#8B5CF6]" /> Legal & Privacy Management
        </h2>
        <p className="text-gray-500 text-sm">Configure consent rules, manage legal pages, and control footer links</p>
      </div>
      <ZoneManager />
      <LegalPagesManager />
    </div>
  );
};

export default LegalPrivacyPanel;
